import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import {
  formatMeterRaw,
  formatTradingDay,
} from './dailyDataUtils'
import DailyDataNav from './DailyDataNav'
import MeterReconciliationPanel from './MeterReconciliationPanel'

async function fetchReconciliation(tradingDay) {
  const params = new URLSearchParams()
  if (tradingDay) params.set('trading_day', tradingDay)
  const qs = params.toString()
  const res = await authFetch(
    getLocalApiUrl(`/api/daily-data/meter/reconciliation${qs ? `?${qs}` : ''}`)
  )
  const json = await res.json()
  if (!json.success) throw new Error(json.error || 'Failed to load turnover')
  return json.data
}

function DayTurnoverCard({ label, report, selected, onSelect, loading }) {
  const day = report?.trading_day
  const turnover = report?.reconciliation?.totals?.turnover
  const hasData = (report?.reconciliation?.completeness?.distinct_egms || 0) > 0
  const partial = !!report?.reconciliation?.completeness?.partial_day

  return (
    <button
      type="button"
      onClick={() => day && onSelect(day)}
      disabled={!day || loading}
      className={`text-left rounded-xl p-5 border transition-colors disabled:opacity-60 ${
        selected
          ? 'bg-indigo-50 border-indigo-300 ring-2 ring-indigo-200'
          : 'bg-white border-gray-200 hover:border-indigo-200 hover:bg-indigo-50/40'
      }`}
    >
      <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-gray-500 mt-1">
        {day ? formatTradingDay(day) : loading ? 'Loading…' : '—'}
      </p>
      <p className="text-3xl font-bold text-gray-900 mt-3 tabular-nums">
        {loading && !report ? '…' : hasData ? formatMeterRaw(turnover) : '—'}
      </p>
      <p className="text-xs text-gray-500 mt-2">
        {hasData
          ? `${report.reconciliation.completeness.distinct_egms} EGMs${partial ? ' · Partial day' : ''}`
          : report
            ? 'No meter data'
            : ''}
      </p>
    </button>
  )
}

export default function DailyTurnover() {
  const [searchParams, setSearchParams] = useSearchParams()
  const urlDay = searchParams.get('day') || ''

  const [todayReport, setTodayReport] = useState(null)
  const [yesterdayReport, setYesterdayReport] = useState(null)
  const [otherReport, setOtherReport] = useState(null)
  const [loadingBootstrap, setLoadingBootstrap] = useState(true)
  const [loadingOther, setLoadingOther] = useState(false)
  const [error, setError] = useState(null)
  const [tradingDay, setTradingDay] = useState(urlDay)
  const [calendarDay, setCalendarDay] = useState(urlDay)
  const [clearingDay, setClearingDay] = useState(null)
  const [showClearConfirm, setShowClearConfirm] = useState(false)

  const today = todayReport?.trading_day || ''
  const yesterday = yesterdayReport?.trading_day || ''
  const timezone = todayReport?.timezone || yesterdayReport?.timezone || otherReport?.timezone

  const selectedReport = useMemo(() => {
    if (!tradingDay) return null
    if (tradingDay === today) return todayReport
    if (tradingDay === yesterday) return yesterdayReport
    if (otherReport?.trading_day === tradingDay) return otherReport
    return null
  }, [tradingDay, today, yesterday, todayReport, yesterdayReport, otherReport])

  const loadBootstrap = useCallback(async () => {
    setLoadingBootstrap(true)
    setError(null)
    try {
      const [todayData, yesterdayData] = await Promise.all([
        fetchReconciliation(null),
        fetchReconciliation('yesterday'),
      ])
      setTodayReport(todayData)
      setYesterdayReport(yesterdayData)
      setTradingDay((prev) => {
        if (prev) return prev
        return yesterdayData?.trading_day || todayData?.trading_day || ''
      })
      setCalendarDay((prev) => {
        if (prev) return prev
        return yesterdayData?.trading_day || todayData?.trading_day || ''
      })
      return { todayData, yesterdayData }
    } catch (e) {
      setError(e.message)
      setTodayReport(null)
      setYesterdayReport(null)
      return null
    } finally {
      setLoadingBootstrap(false)
    }
  }, [])

  useEffect(() => {
    loadBootstrap()
  }, [loadBootstrap])

  useEffect(() => {
    if (tradingDay) {
      setSearchParams({ day: tradingDay }, { replace: true })
      setCalendarDay(tradingDay)
    }
  }, [tradingDay, setSearchParams])

  // Load a calendar-picked day that isn't today/yesterday
  useEffect(() => {
    if (!tradingDay || loadingBootstrap) return
    if (tradingDay === today || tradingDay === yesterday) {
      setOtherReport(null)
      return
    }
    if (otherReport?.trading_day === tradingDay) return

    let cancelled = false
    ;(async () => {
      setLoadingOther(true)
      setError(null)
      try {
        const data = await fetchReconciliation(tradingDay)
        if (!cancelled) setOtherReport(data)
      } catch (e) {
        if (!cancelled) {
          setError(e.message)
          setOtherReport(null)
        }
      } finally {
        if (!cancelled) setLoadingOther(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [tradingDay, today, yesterday, loadingBootstrap, otherReport?.trading_day])

  const selectDay = (day) => {
    if (!day) return
    setTradingDay(day)
    setCalendarDay(day)
  }

  const handleCalendarChange = (value) => {
    setCalendarDay(value)
    if (value) selectDay(value)
  }

  const handleRefresh = async () => {
    const current = tradingDay
    const boot = await loadBootstrap()
    if (!boot || !current) return
    const todayDay = boot.todayData?.trading_day
    const yesterdayDay = boot.yesterdayData?.trading_day
    if (current === todayDay || current === yesterdayDay) return
    setLoadingOther(true)
    try {
      const data = await fetchReconciliation(current)
      setOtherReport(data)
      setTradingDay(current)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoadingOther(false)
    }
  }

  const handleClearDay = async () => {
    if (!tradingDay) return
    setClearingDay(tradingDay)
    setShowClearConfirm(false)
    try {
      const params = new URLSearchParams({ trading_day: tradingDay })
      const res = await authFetch(getLocalApiUrl(`/api/daily-data/meter/clear-day?${params}`), {
        method: 'DELETE',
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to clear data')
      const parts = []
      if (json.meter_deleted) parts.push(`${json.meter_deleted} meter readings`)
      if (json.slot_deleted) parts.push(`${json.slot_deleted} slot analysis records`)
      if (json.import_runs_deleted) parts.push(`${json.import_runs_deleted} import runs`)
      const msg = parts.length > 0
        ? `✓ Cleared ${tradingDay}: ${parts.join(', ')}`
        : `✓ ${json.message || 'Data cleared'}`
      alert(msg)
      await handleRefresh()
    } catch (e) {
      alert(`Failed to clear data: ${e.message}`)
    } finally {
      setClearingDay(null)
    }
  }

  const hasSelectedData = (selectedReport?.reconciliation?.completeness?.distinct_egms || 0) > 0
  const selectedTurnover = selectedReport?.reconciliation?.totals?.turnover
  const loadingSelected =
    loadingBootstrap ||
    (loadingOther && tradingDay && tradingDay !== today && tradingDay !== yesterday)

  return (
    <div className="space-y-6">
      <DailyDataNav />

      <div className="bg-white shadow rounded-lg p-6 border-l-4 border-indigo-600">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-indigo-50 p-2 border border-indigo-100">
              <ChartBarIcon className="w-7 h-7 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Daily turnover</h1>
              <p className="text-gray-600 mt-1">
                Today and yesterday at a glance — pick any other trading day from the calendar.
              </p>
              {timezone && (
                <p className="text-xs text-gray-500 mt-2">
                  Timezone: {timezone}
                  {' · '}
                  <Link to="/monitor-safe/daily-data/meter" className="text-primary-600 hover:underline">
                    Meter import
                  </Link>
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loadingBootstrap || loadingOther}
            className="inline-flex items-center px-3 py-2 border rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-1 ${loadingBootstrap || loadingOther ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800">{error}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DayTurnoverCard
          label="Today"
          report={todayReport}
          selected={tradingDay === today}
          onSelect={selectDay}
          loading={loadingBootstrap}
        />
        <DayTurnoverCard
          label="Yesterday"
          report={yesterdayReport}
          selected={tradingDay === yesterday}
          onSelect={selectDay}
          loading={loadingBootstrap}
        />
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-wrap items-end gap-4 mb-6">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
              Look up another trading day
            </label>
            <input
              type="date"
              value={calendarDay || ''}
              onChange={(e) => handleCalendarChange(e.target.value)}
              className="w-full max-w-xs border rounded-md px-3 py-2 text-sm"
            />
          </div>
          {tradingDay && hasSelectedData && (
            <button
              type="button"
              onClick={() => setShowClearConfirm(true)}
              disabled={!!clearingDay}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-md disabled:opacity-50"
            >
              <TrashIcon className="w-4 h-4 mr-1" />
              {clearingDay ? 'Clearing…' : 'Clear this day'}
            </button>
          )}
        </div>

        {tradingDay && (
          <div
            className={`rounded-xl p-6 ${
              hasSelectedData
                ? 'bg-indigo-50 border border-indigo-200'
                : 'bg-gray-50 border border-gray-200'
            }`}
          >
            <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">Turnover</p>
            <p className="text-4xl font-bold text-gray-900 mt-1 tabular-nums">
              {loadingSelected ? '…' : hasSelectedData ? formatMeterRaw(selectedTurnover) : '—'}
            </p>
            <p className="text-sm text-gray-600 mt-2">
              {formatTradingDay(tradingDay)}
              {selectedReport?.reconciliation?.completeness?.partial_day && (
                <span className="ml-2 text-amber-700">· Partial day (more imports may change this)</span>
              )}
              {selectedReport && !hasSelectedData && !loadingSelected && (
                <span className="ml-2 text-gray-500">· No meter data ingested for this day</span>
              )}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Meter counter units (typically cents). Not dollars until converted in your venue reporting.
            </p>
          </div>
        )}
      </div>

      {tradingDay && (
        <MeterReconciliationPanel
          tradingDay={tradingDay}
          onTradingDayChange={selectDay}
          prefetchedReport={selectedReport}
          prefetchedLoading={loadingSelected}
          skipOwnFetch
          title="Full reconciliation"
          showDatePicker={false}
        />
      )}

      {showClearConfirm && tradingDay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <TrashIcon className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Clear all data for this day?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  This will permanently delete all meter data for{' '}
                  <strong className="text-gray-900">{formatTradingDay(tradingDay)}</strong>.
                </p>
                {hasSelectedData && (
                  <div className="bg-gray-50 rounded p-3 mb-4 text-sm">
                    <p className="text-gray-700">
                      <strong>This will delete:</strong>
                    </p>
                    <ul className="mt-1 space-y-1 text-gray-600">
                      <li>
                        • ~{selectedReport.reconciliation.completeness.distinct_egms || 0} EGMs worth of
                        meter readings
                      </li>
                      <li>• All quarter-hour snapshots for this day</li>
                      <li>• Slot analysis data (if any)</li>
                      <li>• Import run history for this day</li>
                    </ul>
                  </div>
                )}
                <p className="text-xs text-gray-500">You can re-import the data later if needed.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowClearConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleClearDay}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Yes, clear this day
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
